<template>
  <div class="product-page">
    <section class="p-hero reveal">
      <SectionBackdrop :bg="bgHero" variant="light" :count="32" />
      <div class="container p-hero-inner">
        <div class="p-hero-copy">
          <div class="p-hero-badge">
            <img :src="logo" alt="周易星运八字测算大师" />
            <span>周易星运八字测算大师</span>
          </div>
          <h1>周易星运八字测算大师</h1>
          <p class="p-hero-lead">融汇周易智慧与传统历法，提供黄历查询、八字排盘、星座测算等国学文化工具，助您知命顺时、趋吉避凶。</p>
          <div class="p-hero-features">
            <p v-for="line in featureLines" :key="line" class="p-hero-feature">{{ line }}</p>
          </div>
          <div class="p-hero-actions">
            <a href="/ftp/packages/fatestar/FateStarSetup.exe" download="FateStarSetup.exe" class="btn-download">
              <img src="@/assets/videocvt/Windows.png" alt="Windows" class="btn-os-icon" />
              <span>免费下载</span>
            </a>
          </div>
          <p class="p-hero-system">支持系统：Windows 7 / 8 / 10 / 11</p>
          <div class="p-hero-meta">
            <span v-for="item in metaItems" :key="item.label" class="p-hero-meta-item">
              <span class="meta-icon">
                <FeatureIcon :name="item.icon" :alt="item.label" size="sm" />
              </span>
              {{ item.label }}
            </span>
          </div>
        </div>
        <div class="p-hero-visual shot-glow">
          <img :src="heroImage" alt="周易星运八字测算大师主界面" class="app-shot" loading="lazy" />
        </div>
      </div>
    </section>

    <section class="p-video reveal">
      <SectionBackdrop :bg="bgFeatures" variant="light" :count="16" />
      <div class="container">
        <div class="p-video-head">
          <p class="overline">Demo</p>
          <h2>产品演示</h2>
          <p>以直观视频展示核心功能，感受传统国学与现代软件的结合之美。</p>
        </div>
        <div class="p-video-grid">
          <div class="p-video-wrap">
            <video :src="demoVideo" controls playsinline preload="metadata" :poster="posterImage">
              您的浏览器不支持视频播放。
            </video>
          </div>
          <div class="p-video-copy">
            <h3>传承国学 · 以易观世</h3>
            <p>《周易》被誉为"群经之首"，以阴阳五行、天干地支为根基，阐释天地万物运行规律。本产品将千年国学智慧融入日常工具，让黄历宜忌、八字命理、星座星运触手可及。</p>
            <p>无论是择吉日、排八字、为宝宝取名，还是了解生肖运势与缘分匹配，我们都力求在尊重传统文化的基础上，提供清晰易懂的解读与指引。</p>
            <a href="/ftp/packages/fatestar/FateStarSetup.exe" download="FateStarSetup.exe" class="btn-download btn-download-sm">
              <img src="@/assets/videocvt/Windows.png" alt="Windows" class="btn-os-icon" />
              <span>免费下载</span>
            </a>
          </div>
        </div>
      </div>
    </section>

    <section class="p-features">
      <SectionBackdrop :bg="bgFeatures" variant="light" :count="20" />
      <div class="container">
        <div class="p-features-head reveal">
          <p class="overline">Features</p>
          <h2>核心功能</h2>
          <p>六大模块覆盖日常国学文化需求，界面简洁，解读清晰，上手即用。</p>
        </div>

        <div class="p-other reveal">
          <div class="p-other-grid p-other-grid--3">
            <article v-for="item in coreFeatures" :key="item.title" class="p-other-card">
              <div class="p-other-icon">
                <FeatureIcon :name="item.icon" :alt="item.title" size="lg" />
              </div>
              <div>
                <h4>{{ item.title }}</h4>
                <p>{{ item.description }}</p>
              </div>
            </article>
          </div>

          <div class="p-culture reveal">
            <article v-for="item in cultureBlocks" :key="item.title" class="p-culture-card">
              <h4>{{ item.title }}</h4>
              <p>{{ item.content }}</p>
            </article>
          </div>
        </div>
      </div>
    </section>

    <section class="p-reviews reveal">
      <SectionBackdrop :bg="bgReviews" variant="dark" :count="30" />
      <div class="container">
        <div class="p-reviews-head">
          <h3>用户给我们的评价</h3>
          <p>真实反馈持续驱动产品体验优化，让国学工具更好服务于日常生活。</p>
        </div>
        <div class="p-reviews-viewport">
          <div class="p-reviews-track" :style="{ transform: `translateX(-${activeReviewGroup * 100}%)` }">
            <div v-for="(group, gi) in reviewGroups" :key="`g-${gi}`" class="p-review-group">
              <article v-for="item in group" :key="item.name" class="p-review-card">
                <div class="p-review-user">
                  <div class="p-review-avatar">
                    <img :src="item.avatar" :alt="`${item.name}头像`" />
                  </div>
                  <div>
                    <div class="p-review-name">{{ item.name }}</div>
                    <div class="p-review-stars" aria-label="5 stars">
                      <span v-for="n in 5" :key="n">★</span>
                    </div>
                  </div>
                </div>
                <p class="p-review-text">{{ item.comment }}</p>
              </article>
            </div>
          </div>
        </div>
        <div class="p-review-dots">
          <button
            v-for="(group, index) in reviewGroups"
            :key="`dot-${index}`"
            class="p-review-dot"
            :class="{ active: activeReviewGroup === index }"
            @click="activeReviewGroup = index"
          ></button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import FeatureIcon from '@/components/FeatureIcon.vue'
import SectionBackdrop from '@/components/SectionBackdrop.vue'

export default {
  name: 'FateStar',
  components: { FeatureIcon, SectionBackdrop },
  data() {
    return {
      observer: null,
      reviewTimer: null,
      activeReviewGroup: 0,
      bgHero: require('@/assets/illustrations/bg-product-hero.svg'),
      bgFeatures: require('@/assets/illustrations/bg-product-features.svg'),
      bgReviews: require('@/assets/illustrations/bg-standards-glow.svg'),
      logo: require('@/assets/fatestar/fatestar_48x48.png'),
      heroImage: require('@/assets/fatestar/首页.png'),
      demoVideo: require('@/assets/fatestar/星运八字.mp4'),
      posterImage: require('@/assets/fatestar/首页.png'),
      featureLines: [
        '黄历查询 / 星座测算 / 八字排盘 / 宝宝取名',
        '缘分配对 / 生肖运势 / 节气宜忌 / 传统文化解读'
      ],
      metaItems: [
        { icon: 'yinyang', label: '周易智慧' },
        { icon: 'calendar', label: '黄历宜忌' },
        { icon: 'sparkle', label: '精准测算' }
      ],
      coreFeatures: [
        { title: '黄历查询', description: '每日宜忌、节气节日、冲煞方位一目了然，助您择吉而行。', icon: 'calendar' },
        { title: '星座测算', description: '基于出生日期解析星座特质，提供性格与运势参考。', icon: 'star' },
        { title: '八字排盘', description: '输入生辰自动排盘，展示四柱、十神、大运等命理信息。', icon: 'yinyang' },
        { title: '宝宝取名', description: '结合五行喜用神与音律寓意，为新生儿推荐吉祥好名。', icon: 'tag' },
        { title: '缘分配对', description: '从生肖、星座、八字等维度分析缘分契合度与相处建议。', icon: 'heart' },
        { title: '生肖运势', description: '按生肖查询年运、月运与每日运势，把握时机趋吉避凶。', icon: 'sparkle' }
      ],
      cultureBlocks: [
        {
          title: '知命而不认命',
          content: '《易经》有云："君子以自强不息。"命理学旨在帮助我们认识自身禀赋与时机规律，而非消极宿命。了解八字五行，是为了更好地扬长避短、顺势而为。'
        },
        {
          title: '择吉而行',
          content: '黄历文化源远流长，记载每日宜忌、吉神凶煞。结婚嫁娶、搬家入宅、开业出行，择一吉日可增添心安与仪式感，让重要时刻更有纪念意义。'
        },
        {
          title: '名以载道',
          content: '取名是中华文化的独特传承。好名字承载父母期望，兼顾音韵、字义与五行平衡。为宝宝取一个寓意深远、朗朗上口的名字，是送给孩子最好的礼物之一。'
        }
      ],
      reviews: [
        { name: '王女士', comment: '黄历查询很方便，每天出门前看看宜忌，心里更有数了。', avatar: require('@/assets/fatestar/avatar1.jpg') },
        { name: '李先生', comment: '八字排盘界面清晰，大运流年展示直观，比网上零散工具好用。', avatar: require('@/assets/fatestar/avatar2.jpg') },
        { name: '张同学', comment: '星座和生肖运势都有，功能集中在一个软件里，不用来回找网页了。', avatar: require('@/assets/fatestar/avatar3.jpeg') },
        { name: '陈女士', comment: '给宝宝取名时参考了五行建议，全家都很满意，寓意也好。', avatar: require('@/assets/fatestar/avatar4.jpeg') },
        { name: '刘先生', comment: '缘分配对挺有趣的，和对象一起测测契合度，增添了不少话题。', avatar: require('@/assets/fatestar/avatar5.jpg') },
        { name: '赵女士', comment: '国学文案解读通俗易懂，既能用工具，也能学到传统文化知识。', avatar: require('@/assets/fatestar/avatar6.jpeg') }
      ]
    }
  },
  computed: {
    reviewGroups() {
      return [this.reviews.slice(0, 3), this.reviews.slice(3, 6)]
    }
  },
  mounted() {
    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            this.observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.12 }
    )
    this.$el.querySelectorAll('.reveal').forEach(el => this.observer.observe(el))
    this.reviewTimer = setInterval(() => {
      this.activeReviewGroup = this.activeReviewGroup === 0 ? 1 : 0
    }, 4200)
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect()
    if (this.reviewTimer) clearInterval(this.reviewTimer)
  }
}
</script>
